'use client'

import React from 'react'
import {
  ExternalLink,
  FileText,
  Calendar,
  Briefcase,
  DollarSign,
  ArrowRight,
  ScanSearch,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { isAnalyticsConsentGranted } from '@/lib/consent-manager'
import { Button } from '@/components/ui/button'

export type ActionCardType =
  | 'contact-form'
  | 'calendly'
  | 'portfolio'
  | 'pricing'
  | 'free-check'
  | 'custom'

interface ActionCardProps {
  type: ActionCardType
  title?: string
  description?: string
  url?: string
  buttonText?: string
  tags?: string[]
  className?: string
}

/**
 * ActionCard - Generic card component for chat actions (contact form, calendly, pages, etc.)
 * Opens links in a new tab to preserve chat context
 */
export default function ActionCard({
  type,
  title,
  description,
  url,
  buttonText,
  tags,
  className,
}: ActionCardProps) {
  // Default configurations for each card type
  const cardConfigs: Record<
    ActionCardType,
    {
      icon: React.ComponentType<{ className?: string }>
      defaultTitle: string
      defaultDescription: string
      defaultUrl: string
      defaultButtonText: string
      defaultTags: string[]
    }
  > = {
    'contact-form': {
      icon: FileText,
      defaultTitle: 'Get a Custom Quote',
      defaultDescription:
        "Fill out our contact form and we'll send you a detailed proposal within 24 hours.",
      defaultUrl: '/contact-us',
      defaultButtonText: 'Open Contact Form',
      defaultTags: ['Project Details', 'Timeline & Budget', 'Custom Quote'],
    },
    calendly: {
      icon: Calendar,
      defaultTitle: 'Book a Discovery Call',
      defaultDescription:
        'Schedule a 30-minute call with Lloyd to discuss your project and get a custom strategy.',
      defaultUrl: 'https://calendly.com/lloyd-pixelmojo/30min',
      defaultButtonText: 'Book a Call',
      defaultTags: ['30 Minutes', 'Free Consultation', 'Custom Strategy'],
    },
    portfolio: {
      icon: Briefcase,
      defaultTitle: 'View Our Portfolio',
      defaultDescription:
        "Explore our recent projects and see how we've helped clients achieve their goals.",
      defaultUrl: '/projects',
      defaultButtonText: 'View Portfolio',
      defaultTags: ['Case Studies', 'Recent Work', 'Client Results'],
    },
    pricing: {
      icon: DollarSign,
      defaultTitle: 'View Pricing',
      defaultDescription:
        'See our transparent pricing packages and find the right fit for your project.',
      defaultUrl: '/pricing',
      defaultButtonText: 'View Pricing',
      defaultTags: ['Transparent Pricing', 'Packages', 'Custom Quotes'],
    },
    'free-check': {
      icon: ScanSearch,
      defaultTitle: 'Run a Free AI Visibility Check',
      defaultDescription:
        'Scan the site across Radar and see how AI search engines actually read it. The report gets emailed to you.',
      defaultUrl: '/platform',
      defaultButtonText: 'Run the free check',
      defaultTags: ['Free', 'Radar Audit', 'Report by Email'],
    },
    custom: {
      icon: ArrowRight,
      defaultTitle: title || 'Learn More',
      defaultDescription: description || 'Click to learn more.',
      defaultUrl: url || '#',
      defaultButtonText: buttonText || 'Open',
      defaultTags: tags || [],
    },
  }

  const config = cardConfigs[type]
  const Icon = config.icon

  const finalTitle = title || config.defaultTitle
  const finalDescription = description || config.defaultDescription
  const finalUrl = url || config.defaultUrl
  const finalButtonText = buttonText || config.defaultButtonText
  const finalTags = tags || config.defaultTags

  const handleClick = () => {
    // Open in new tab to preserve chat context
    window.open(finalUrl, '_blank', 'noopener,noreferrer')

    // Track click if analytics available and consent granted
    if (
      typeof window !== 'undefined' &&
      isAnalyticsConsentGranted() &&
      window.gtag
    ) {
      window.gtag('event', 'action_card_clicked', {
        event_category: 'chat_interaction',
        event_label: `${type}_card`,
        card_type: type,
        card_url: finalUrl,
        value: 1,
      })
    }
  }

  return (
    <div
      className={cn(
        'border border-border rounded-xl bg-card p-4 md:p-5 shadow-sm hover:shadow-md transition-all duration-200',
        className
      )}
    >
      <div className='flex items-start gap-3 mb-4'>
        <div className='flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center'>
          <Icon className='w-5 h-5 text-primary' />
        </div>
        <div className='flex-1 min-w-0'>
          <h3 className='font-semibold text-base text-foreground mb-1'>
            {finalTitle}
          </h3>
          <p className='text-sm text-muted-foreground'>{finalDescription}</p>
        </div>
      </div>

      <div className='space-y-3'>
        {/* Tags */}
        {finalTags.length > 0 && (
          <div className='flex flex-wrap gap-2'>
            {finalTags.map((tag, index) => (
              <span
                key={index}
                className='text-xs px-2 py-1 bg-muted text-muted-foreground rounded-md'
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* CTA Button */}
        <Button
          onClick={handleClick}
          variant='cta'
          size='sm'
          className='w-full rounded-lg'
          rightIcon={<ExternalLink className='w-4 h-4' />}
          aria-label={finalButtonText}
        >
          {finalButtonText}
        </Button>

        <p className='text-xs text-muted-foreground text-center'>
          Opens in a new tab • Your chat will stay open
        </p>
      </div>
    </div>
  )
}
